import { useMemo } from 'react';

import { useHdrProcessingRateMins } from '@/api/devices';
import {
    BaroPressureItemFormattedWithUnits,
    HistoricalBarometricPressureItem,
    ProcessingSource,
} from '@/api/historicalData';
import { combineProcessingSources } from '@/api/historicalData/helpers';
import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { convert } from '@/common/convert';
import { CombinedQueryResult, QueryConfig } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { HISTORICAL_BAROMETRIC_PRESSURE_DATA_QUERY_KEY } from '@/config';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';
import { DateRange, useGlobalStore } from '@/store';
import { maybeMockHdrResponse } from '@/utils/mockDataHelpers';

export type GetHistoricalBarometricPressureParams = {
    spotterId: string;
    startDate: string;
    endDate: string;
    processingSources: ProcessingSource | 'all';
};

export const getHistoricalBarometricPressure = async (
    params: GetHistoricalBarometricPressureParams,
    options?: RequestOptions
): Promise<HistoricalBarometricPressureItem[]> => {
    const { data } = await maybeMockHdrResponse(
        axios.get<HistoricalBarometricPressureItem[], APIResponse<HistoricalBarometricPressureItem[]>>(
            '/fetch/barometer-data-history',
            {
                params,
                ...requestConfig(options),
            }
        )
    );
    return data;
};

type UseHistoricalBarometricPressureDataOptions = {
    spotterId?: string | null;
    dateRange?: DateRange | null;
    processingSources: ProcessingSource | 'all';
    config?: Omit<QueryConfig<typeof getHistoricalBarometricPressure>, 'select'>;
};

/**
 * Historical barometric pressure for the range, in the user's preferred units, with embedded samples filling
 * the gaps between the hdr ones.
 */
export const useHistoricalBarometricPressureData = ({
    spotterId,
    dateRange,
    processingSources,
    config,
}: UseHistoricalBarometricPressureDataOptions): CombinedQueryResult<BaroPressureItemFormattedWithUnits[]> => {
    const processingSource = useGlobalStore((state) => state.processingSource);
    const { data: barometricPressureRaw, ...rest } = useLiveSplitQuery({
        queryKeyPrefix: HISTORICAL_BAROMETRIC_PRESSURE_DATA_QUERY_KEY,
        spotterId,
        dateRange,
        keyOptions: { processingSources },
        fetchRange: ({ viewToken, signal, ...range }) =>
            getHistoricalBarometricPressure({ ...range, processingSources }, { viewToken, signal }),
        // one of the older Spotter entrypoints, so it excludes the bounds it is given
        apiDateBounds: 'exclusive',
        timestampKey: 'timestamp',
        sortOrder: 'asc',
        /*
         * wavefleet sorts this window oldest first across every device row it queried. Ordered here so the
         * trimming and clipping that reassemble the windows own the order they need.
         */
        responseSortOrder: 'unsorted',
        config,
    });
    const hdrProcessingRateMins = useHdrProcessingRateMins({
        spotterId,
        config: { enabled: config?.enabled !== false },
    });
    const units = usePreferredUnits('airPressure');
    const formatted = useMemo<BaroPressureItemFormattedWithUnits[] | undefined>(
        () =>
            combineProcessingSources(
                barometricPressureRaw?.map((item) => {
                    const pressure = convert(item.value).from(item.units).to(units);
                    return {
                        ...item,
                        value: pressure,
                        units,
                        unitType: 'airPressure',
                    };
                }),
                hdrProcessingRateMins,
                processingSource
            ),
        [barometricPressureRaw, units, hdrProcessingRateMins, processingSource]
    );

    return { data: formatted, ...rest };
};
